import React from 'react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiCheck, FiChevronDown } from 'react-icons/fi';
import styled, { createGlobalStyle } from 'styled-components';

// Styled Components
const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 20px;
  border-radius: 8px;
  transform: translateY(100%);
  opacity: 0;
  transition: transform 0.3s ease, opacity 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow-y: auto;

  h4 {
    margin: 0 0 10px;
    font-size: 18px;
    color: var(--theme-color);
  }

  ul {
    list-style-type: none;
    padding: 0;
    margin: 0;
  }

  li {
    margin-bottom: 8px;
    position: relative;
    padding-left: 20px;
  }

  li:before {
    content: "•";
    color: var(--theme-color);
    position: absolute;
    left: 0;
    font-weight: bold;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  margin: 20px 0;
  max-width: 400px; /* Adjusted for smaller image size */
  height: 300px; /* Fixed height for consistency */
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover ${ImageOverlay} {
    transform: translateY(0);
    opacity: 1;
  }

  .content-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover; /* Ensures images scale properly */
    border-radius: 8px;
  }
`;

// Global Styles
const GlobalStyles = createGlobalStyle`
  :root {
    --theme-color: #f7bf68;
  }
  /* Insurance Content Section */
  .insurance-content-section {
    padding: 80px 0;
    background-color: #f9f9f9;
  }

  .insurance-tabs {
    display: flex;
    margin-bottom: 40px;
    border-bottom: 1px solid #ddd;
    flex-wrap: wrap;
  }

  .tab-btn {
    padding: 12px 24px;
    margin-right: 10px;
    background: none;
    border: none;
    border-bottom: 3px solid transparent;
    font-size: 16px;
    font-weight: 600;
    color: #666;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .tab-btn:hover,
  .tab-btn.active {
    color: var(--theme-color);
    border-bottom-color: var(--theme-color);
  }

  .content-section {
    background: white;
    padding: 40px;
    border-radius: 8px;
    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
    margin-bottom: 30px;
  }

  .section-header {
    margin-bottom: 30px;
    text-align: center;
  }

  .section-header h2 {
    color: var(--theme-color);
    margin-bottom: 10px;
  }

  .subtitle {
    color: #666;
    font-size: 18px;
  }

  .content-box {
    margin-bottom: 30px;
  }

  .highlight-box {
    background-color: #fff8ec;
    padding: 25px;
    border-radius: 8px;
    border-left: 4px solid var(--theme-color);
  }

  /* Tables */
  .styled-table {
    width: 100%;
    border-collapse: collapse;
    margin: 20px 0;
    font-size: 0.9em;
    min-width: 400px;
    border-radius: 5px 5px 0 0;
    overflow: hidden;
    box-shadow: 0 0 20px rgba(0,0,0,0.05);
  }

  .styled-table thead tr {
    background-color: var(--theme-color);
    color: #ffffff;
    text-align: left;
    font-weight: bold;
  }

  .styled-table th,
  .styled-table td {
    padding: 12px 15px;
  }

  .styled-table tbody tr {
    border-bottom: 1px solid #dddddd;
  }

  .styled-table tbody tr:nth-of-type(even) {
    background-color: #f3f3f3;
  }

  .styled-table tbody tr:last-of-type {
    border-bottom: 2px solid var(--theme-color);
  }

  .styled-table tbody tr.active-row {
    font-weight: bold;
    color: var(--theme-color);
  }

  .expand-table-btn {
    background: none;
    border: none;
    color: var(--theme-color);
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    margin: 10px 0;
  }

  .expand-table-btn svg {
    margin-left: 5px;
    transition: transform 0.3s ease;
  }

  .expand-table-btn .rotate {
    transform: rotate(180deg);
  }

  .table-responsive {
    max-height: 400px;
    overflow: hidden;
    transition: max-height 0.5s ease;
  }

  .table-responsive.expanded {
    max-height: none;
  }

  /* Lists */
  .styled-list {
    list-style-type: none;
    padding-left: 0;
  }

  .styled-list li {
    position: relative;
    padding-left: 25px;
    margin-bottom: 10px;
    line-height: 1.6;
  }

  .styled-list li:before {
    content: "•";
    color: var(--theme-color);
    font-weight: bold;
    position: absolute;
    left: 0;
  }

  /* Checklist */
  .checklist {
    margin: 20px 0;
  }

  .check-item {
    display: flex;
    align-items: center;
    margin-bottom: 5px;
  }

  .check-icon {
    color: var(--theme-color);
    margin-right: 10px;
    font-size: 18px;
  }

  /* Cards */
  .provider-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
    margin: 30px 0;
  }

  .provider-card {
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
    border-top: 3px solid var(--theme-color);
  }

  .provider-card h3 {
    color: var(--theme-color);
    margin-top: 0;
  }

  .scenario-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
    margin: 20px 0;
  }

  .scenario-card {
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
    border-left: 3px solid var(--theme-color);
  }

  .insurance-types {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
    margin: 20px 0;
  }

  .type-card {
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
    border: 1px solid #eee;
  }

  .type-card h4 {
    color: var(--theme-color);
    margin-top: 0;
  }

  /* Pros and Cons */
  .pros-cons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    margin: 20px 0;
  }

  .pros {
    background: #fff8ec;
    padding: 20px;
    border-radius: 8px;
    border-left: 4px solid var(--theme-color);
  }

  .cons {
    background: #fff8ec;
    padding: 20px;
    border-radius: 8px;
    border-left: 4px solid var(--theme-color);
  }

  .pros h4 {
    color: var(--theme-color);
    margin-top: 0;
  }

  .cons h4 {
    color: var(--theme-color);
    margin-top: 0;
  }

  /* Footnotes */
  .footnote {
    font-size: 0.85em;
    color: #666;
    font-style: italic;
    margin-top: 5px;
  }

  /* Image Styling */
  .content-image {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 20px 0;
    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  }

  /* Responsive */
  @media (max-width: 768px) {
    .insurance-tabs {
      flex-direction: column;
    }
    
    .tab-btn {
      margin-bottom: 10px;
      text-align: left;
    }
    
    .pros-cons {
      grid-template-columns: 1fr;
    }
    
    .content-section {
      padding: 20px;
    }

    .content-image {
      max-width: 100%;
    }

    ${ImageWrapper} {
      max-width: 100%; /* Ensure responsiveness on smaller screens */
      height: 200px; /* Smaller height for mobile */
    }
  }
`;

export default function LifeHealthAnnuityInsurance() {
    const [isLoading, setIsLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('annuities');
    const [expandedTables, setExpandedTables] = useState({});

    useEffect(() => {
        setTimeout(() => {
            setIsLoading(false);
        }, 2000);
    }, []);

    const toggleTableExpand = (tableId) => {
        setExpandedTables(prev => ({
            ...prev,
            [tableId]: !prev[tableId]
        }));
    };

    const fadeIn = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    const sectionVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
    };

    return (
        <div>
            <GlobalStyles />
            <div className="boxed_wrapper ltr">
                {isLoading && (
                    <div className="loader-wrap">
                        <div className="preloader">
                            <div className="preloader-close">close</div>
                            <div id="handle-preloader" className="handle-preloader">
                                <div className="animation-preloader">
                                    <div className="txt-loading">
                                        <img src="assets/images/animation_logo.gif" alt="img" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <Header />

                {/* page-title */}
                <section className="page-title">
                    <div
                        className="bg-layer"
                        style={{
                            backgroundImage: `url("/assets/images/background/page-title.jpg")`,
                        }}
                    ></div>
                    <div className="line-grid-wrap">
                        <span className="line-grid-1"></span>
                        <span className="line-grid-2"></span>
                        <span className="line-grid-3"></span>
                        <span className="line-grid-4"></span>
                        <span className="line-grid-5"></span>
                    </div>
                    <div className="auto-container">
                        <div className="content-box">
                            <h1 style={{ textAlign: "left" }}>Life, Health, & Annuity Insurance</h1>
                            <ul className="bread-crumb clearfix">
                                <li>
                                    <Link to="/">Home</Link>
                                </li>
                                <li>Life, Health, & Annuity Insurance</li>
                            </ul>
                        </div>
                    </div>
                </section>
                {/* Page Title End */}

                {/* Main Content Section */}
                <section className="insurance-content-section">
                    <div className="auto-container">
                        {/* Navigation Tabs */}
                        <motion.div 
                            className="insurance-tabs"
                            initial="hidden"
                            animate="visible"
                            variants={fadeIn}
                        >
                            <button 
                                className={`tab-btn ${activeSection === 'annuities' ? 'active' : ''}`}
                                onClick={() => setActiveSection('annuities')}
                            >
                                Annuities
                            </button>
                            <button 
                                className={`tab-btn ${activeSection === 'health' ? 'active' : ''}`}
                                onClick={() => setActiveSection('health')}
                            >
                                Health Insurance
                            </button>
                            <button 
                                className={`tab-btn ${activeSection === 'keyman' ? 'active' : ''}`}
                                onClick={() => setActiveSection('keyman')}
                            >
                                Keyman Insurance
                            </button>
                            <button 
                                className={`tab-btn ${activeSection === 'life' ? 'active' : ''}`}
                                onClick={() => setActiveSection('life')}
                            >
                                Life Insurance
                            </button>
                        </motion.div>

                        {/* Annuities Section */}
                        {activeSection === 'annuities' && (
                            <motion.div 
                                className="content-section"
                                initial="hidden"
                                animate="visible"
                                variants={sectionVariants}
                            >
                                <motion.div variants={fadeIn} className="section-header">
                                    <h2>Ravline Guide to Annuities in 2025</h2>
                                    <p className="subtitle">Rates, Companies, and Strategic Benefits</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <p>Annuities are essential components of a diversified retirement and wealth strategy. In 2025, annuity products have become more attractive due to rising interest rates and competitive offers from highly rated insurance companies. This guide distills findings from multiple industry sources and articles, presenting a complete outlook for both beginner and advanced investors.</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>What Is an Annuity?</h3>
                                    <p>An annuity is a contract between an individual and an insurance company that guarantees a stream of income in exchange for a lump sum or periodic payments. Annuities are particularly useful for retirement planning due to tax-deferred growth and income guarantees.</p>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/LHI1.jpg" 
                                            alt="Annuity contract illustration" 
                                            className="content-image" 
                                        />
                                        <ImageOverlay>
                                            <h4>Annuity Benefits</h4>
                                            <ul>
                                                <li>Guaranteed income stream.</li>
                                                <li>Tax-deferred growth.</li>
                                                <li>Retirement planning tool.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h4>Types of Annuities:</h4>
                                    <div className={`table-responsive ${expandedTables['annuityTypes'] ? 'expanded' : ''}`}>
                                        <table className="styled-table">
                                            <thead>
                                                <tr>
                                                    <th>Type</th>
                                                    <th>Risk Profile</th>
                                                    <th>Key Feature</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Fixed Annuity</td>
                                                    <td>Low</td>
                                                    <td>Guaranteed interest for set period</td>
                                                </tr>
                                                <tr>
                                                    <td>Fixed Indexed Annuity (FIA)</td>
                                                    <td>Moderate</td>
                                                    <td>Earnings tied to market index, with downside protection</td>
                                                </tr>
                                                <tr>
                                                    <td>Variable Annuity</td>
                                                    <td>High</td>
                                                    <td>Investments in mutual funds, with market exposure</td>
                                                </tr>
                                                <tr>
                                                    <td>Immediate Annuity</td>
                                                    <td>Low</td>
                                                    <td>Starts income payments immediately</td>
                                                </tr>
                                                <tr>
                                                    <td>Deferred Annuity</td>
                                                    <td>Low to moderate</td>
                                                    <td>Income payments delayed, allowing for longer accumulation</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <button 
                                        className="expand-table-btn" 
                                        onClick={() => toggleTableExpand('annuityTypes')}
                                    >
                                        {expandedTables['annuityTypes'] ? 'Show Less' : 'Show More'} <FiChevronDown className={expandedTables['annuityTypes'] ? 'rotate' : ''} />
                                    </button>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>2025's Most Competitive Annuity Rates</h3>
                                    <p>In June 2025, top providers offered the following standout rates:</p>
                                    <ul className="styled-list">
                                        <li><strong>Atlantic Coast Life</strong> – 7.05% for a 10-year fixed annuity (B+ rated)</li>
                                        <li><strong>Knighthead Life</strong> – 6.70% for a 7-year term and 6.45% for a 5-year term (A- rated)</li>
                                        <li><strong>Global Atlantic</strong> – 6.00% for a 1-year MYGA (A rated)</li>
                                        <li><strong>Athene Performance Elite Plus 7</strong> – FIA with 10.82% Future Average Yield</li>
                                        <li><strong>GILICO WealthChoice 10</strong> – FIA cap rate of 12%</li>
                                        <li><strong>Nassau Growth FIA</strong> – FIA cap rate of 11.25%</li>
                                    </ul>
                                    <p>See the chart above for a visual comparison of top rates.</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Best Annuity Providers (2025)</h3>
                                    <div className={`table-responsive ${expandedTables['annuityProviders'] ? 'expanded' : ''}`}>
                                        <table className="styled-table">
                                            <thead>
                                                <tr>
                                                    <th>Provider</th>
                                                    <th>Best For</th>
                                                    <th>Strength</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>New York Life</td>
                                                    <td>Overall reliability</td>
                                                    <td>A++ AM Best</td>
                                                </tr>
                                                <tr>
                                                    <td>MassMutual</td>
                                                    <td>Immediate annuities</td>
                                                    <td>A++ AM Best</td>
                                                </tr>
                                                <tr>
                                                    <td>Athene</td>
                                                    <td>FIAs & fixed annuities</td>
                                                    <td>A financials</td>
                                                </tr>
                                                <tr>
                                                    <td>Gainbridge</td>
                                                    <td>Low-risk, digital MYGAs</td>
                                                    <td>5.80% APY</td>
                                                </tr>
                                                <tr>
                                                    <td>Wichita</td>
                                                    <td>3-5 year MYGAs</td>
                                                    <td>B+ rating, high yield</td>
                                                </tr>
                                                <tr>
                                                    <td>National</td>
                                                    <td></td>
                                                    <td>Uncapped growth models</td>
                                                </tr>
                                                <tr>
                                                    <td>Allianz</td>
                                                    <td>Indexed annuities</td>
                                                    <td></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <button 
                                        className="expand-table-btn" 
                                        onClick={() => toggleTableExpand('annuityProviders')}
                                    >
                                        {expandedTables['annuityProviders'] ? 'Show Less' : 'Show More'} <FiChevronDown className={expandedTables['annuityProviders'] ? 'rotate' : ''} />
                                    </button>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/LHA2.png" 
                                            alt="Top annuity providers" 
                                            className="content-image" 
                                        />
                                        <ImageOverlay>
                                            <h4>Provider Strengths</h4>
                                            <ul>
                                                <li>New York Life: A++ reliability.</li>
                                                <li>MassMutual: Immediate annuities.</li>
                                                <li>Athene: Strong FIA performance.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Why Annuities Now?</h3>
                                    <ul className="styled-list">
                                        <li><strong>Tax-Deferred Growth</strong>: Unlike CDs and bonds, annuities grow without immediate tax.</li>
                                        <li><strong>Market Volatility Protection</strong>: Especially with FIAs, gains are capped but never negative.</li>
                                        <li><strong>Higher Returns</strong>: MYGAs now outpace many CD and Treasury rates. E.g., 5-year annuity at 6.45% vs. Treasury bond at 4.4%.</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <div className={`table-responsive ${expandedTables['annuityComparison'] ? 'expanded' : ''}`}>
                                        <table className="styled-table">
                                            <thead>
                                                <tr>
                                                    <th>Term</th>
                                                    <th>Treasury Bond</th>
                                                    <th>CD</th>
                                                    <th>MYGA (Annuity)</th>
                                                    <th>Tax-Equivalent Yield*</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>3 yrs</td>
                                                    <td>4.80%</td>
                                                    <td>5.10%</td>
                                                    <td>6.00% (Wichita National)</td>
                                                    <td>7.30%</td>
                                                </tr>
                                                <tr>
                                                    <td>5 yrs</td>
                                                    <td>4.40%</td>
                                                    <td>4.90%</td>
                                                    <td>6.45% (Knighthead Life)</td>
                                                    <td>7.70%</td>
                                                </tr>
                                                <tr>
                                                    <td>10 yrs</td>
                                                    <td>4.00%</td>
                                                    <td>N/A</td>
                                                    <td>7.05% (Atlantic Coast)</td>
                                                    <td>8.50%</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <p className="footnote">*Assumes 32% tax bracket</p>
                                    <button 
                                        className="expand-table-btn" 
                                        onClick={() => toggleTableExpand('annuityComparison')}
                                    >
                                        {expandedTables['annuityComparison'] ? 'Show Less' : 'Show More'} <FiChevronDown className={expandedTables['annuityComparison'] ? 'rotate' : ''} />
                                    </button>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h2>Choosing the Right Annuity</h2>
                                    <p>Factors to Consider:</p>
                                    <ul className="styled-list">
                                        <li><strong>Financial Rating</strong> of the insurer (A or better)</li>
                                        <li><strong>Your Age and Premium Amount</strong> – Younger investors may see slightly lower quotes</li>
                                        <li><strong>Purpose</strong> – Income, tax deferral, inheritance</li>
                                        <li><strong>State Regulations</strong> – Rates vary by state</li>
                                        <li><strong>Riders</strong> – GMIB, GMWB, Death Benefit</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box highlight-box">
                                    <h3>Final Insights</h3>
                                    <p>Annuities are not one-size-fits-all. They can provide excellent, low-risk income solutions if selected properly with professional advice. They complement strategies involving IRAs, real estate, and trusts—especially for clients seeking income reliability and tax efficiency.</p>
                                </motion.div>

                                {/* Indexed Annuities Section */}
                                <motion.div variants={fadeIn} className="content-box">
                                    <h2>Ravline 2025 Strategy Guide to Indexed Annuities and Retirement Planning Tools</h2>
                                    <p><strong>Indexed annuities</strong> have rapidly gained favor among retirement-minded investors looking for higher returns than traditional fixed instruments, without full exposure to the stock market. When compared with <strong>Index Universal Life (IUL)</strong> and <strong>Self-Directed Roth IRAs (SDIRAs)</strong>, indexed annuities offer a unique balance of <strong>tax efficiency</strong>, <strong>market participation</strong>, and <strong>income security</strong>. This guide breaks down their features, key differences, and the top providers for 2025.</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>What Is an Indexed Annuity?</h3>
                                    <p>An <strong>indexed annuity</strong> is a contract with an insurance company that provides <strong>principal protection</strong>, tax-deferred growth, and potential interest based on the performance of a stock market index—most commonly the S&P 500.</p>
                                    
                                    <div className="checklist">
                                        <div className="check-item">
                                            <FiCheck className="check-icon" />
                                            <span><strong>Guarantees:</strong></span>
                                        </div>
                                        <ul className="styled-list">
                                            <li>Minimum interest rate: <strong>1–3%</strong></li>
                                            <li>Protection from market loss</li>
                                            <li>Tax-deferred gains</li>
                                        </ul>
                                    </div>

                                    <div className="checklist">
                                        <div className="check-item">
                                            <FiCheck className="check-icon" />
                                            <span><strong>Trade-off:</strong></span>
                                        </div>
                                        <ul className="styled-list">
                                            <li>Gains are <strong>capped</strong> or reduced via participation rates, spreads, or asset fees.</li>
                                        </ul>
                                    </div>

                                    <div className={`table-responsive ${expandedTables['indexedAnnuity'] ? 'expanded' : ''}`}>
                                        <table className="styled-table">
                                            <thead>
                                                <tr>
                                                    <th>Index Performance</th>
                                                    <th>Participation Rate</th>
                                                    <th>Spread/Cap</th>
                                                    <th>Your Return</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>+12%</td>
                                                    <td>80%</td>
                                                    <td>0% Cap</td>
                                                    <td>9.6%</td>
                                                </tr>
                                                <tr>
                                                    <td>+12%</td>
                                                    <td>100%</td>
                                                    <td>3% Cap</td>
                                                    <td>3.0%</td>
                                                </tr>
                                                <tr>
                                                    <td>+12%</td>
                                                    <td>100%</td>
                                                    <td>Spread of 2%</td>
                                                    <td>10.0%</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <button 
                                        className="expand-table-btn" 
                                        onClick={() => toggleTableExpand('indexedAnnuity')}
                                    >
                                        {expandedTables['indexedAnnuity'] ? 'Show Less' : 'Show More'} <FiChevronDown className={expandedTables['indexedAnnuity'] ? 'rotate' : ''} />
                                    </button>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Top Indexed Annuity Providers & Rates (2025)</h3>
                                    <div className={`table-responsive ${expandedTables['indexedProviders'] ? 'expanded' : ''}`}>
                                        <table className="styled-table">
                                            <thead>
                                                <tr>
                                                    <th>Company</th>
                                                    <th>Product</th>
                                                    <th>Cap Rate</th>
                                                    <th>Notes</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Nassau</td>
                                                    <td>Growth Annuity 10</td>
                                                    <td>11.25%</td>
                                                    <td>Strong cap, long-term</td>
                                                </tr>
                                                <tr>
                                                    <td>SILAC</td>
                                                    <td>Denali 7</td>
                                                    <td>11.00%</td>
                                                    <td>Popular for 7-year terms</td>
                                                </tr>
                                                <tr>
                                                    <td>AIG</td>
                                                    <td>Power 7 Protector</td>
                                                    <td>10.75%</td>
                                                    <td>High cap + quality backing</td>
                                                </tr>
                                                <tr>
                                                    <td>Athene</td>
                                                    <td>Performance Elite 7</td>
                                                    <td>10.00%</td>
                                                    <td>Strong historical performance</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <p className="footnote">Source: Aggregated 2025 Market Listings</p>
                                    <button 
                                        className="expand-table-btn" 
                                        onClick={() => toggleTableExpand('indexedProviders')}
                                    >
                                        {expandedTables['indexedProviders'] ? 'Show Less' : 'Show More'} <FiChevronDown className={expandedTables['indexedProviders'] ? 'rotate' : ''} />
                                    </button>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Indexed Annuity vs IUL vs Self-Directed Roth IRA</h3>
                                    <div className={`table-responsive ${expandedTables['annuityComparison2'] ? 'expanded' : ''}`}>
                                        <table className="styled-table">
                                            <thead>
                                                <tr>
                                                    <th>Feature</th>
                                                    <th>Indexed Annuity</th>
                                                    <th>IUL</th>
                                                    <th>SD Roth IRA</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Purpose</td>
                                                    <td>Income + growth</td>
                                                    <td>Insurance + income</td>
                                                    <td>Tax-free growth</td>
                                                </tr>
                                                <tr>
                                                    <td>Growth Link</td>
                                                    <td>Market index (S&P 500)</td>
                                                    <td>Market index</td>
                                                    <td>Any investment</td>
                                                </tr>
                                                <tr>
                                                    <td>Risk</td>
                                                    <td>Low to medium</td>
                                                    <td>Low to medium</td>
                                                    <td>Medium to high</td>
                                                </tr>
                                                <tr>
                                                    <td>Taxation</td>
                                                    <td>Tax-deferred</td>
                                                    <td>Tax-deferred + tax-free income</td>
                                                    <td>Tax-free</td>
                                                </tr>
                                                <tr>
                                                    <td>Contribution Limit</td>
                                                    <td>Premium-dependent</td>
                                                    <td>Premium + death benefit</td>
                                                    <td>$6,500 (2025)</td>
                                                </tr>
                                                <tr>
                                                    <td>Liquidity</td>
                                                    <td>Low before 5–10 years</td>
                                                    <td>Loan-accessible</td>
                                                    <td>High</td>
                                                </tr>
                                                <tr>
                                                    <td>Regulated By</td>
                                                    <td>State Insurance</td>
                                                    <td>State Insurance</td>
                                                    <td>IRS</td>
                                                </tr>
                                                <tr>
                                                    <td>Additional Benefit</td>
                                                    <td>Guaranteed minimum return</td>
                                                    <td>Death benefit</td>
                                                    <td>Self-managed growth</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <button 
                                        className="expand-table-btn" 
                                        onClick={() => toggleTableExpand('annuityComparison2')}
                                    >
                                        {expandedTables['annuityComparison2'] ? 'Show Less' : 'Show More'} <FiChevronDown className={expandedTables['annuityComparison2'] ? 'rotate' : ''} />
                                    </button>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h4>Strategic Positioning:</h4>
                                    <ul className="styled-list">
                                        <li><strong>Annuities:</strong> Better for near-retirees or conservative investors.</li>
                                        <li><strong>IULs:</strong> Suitable for high-income earners needing protection + income.</li>
                                        <li><strong>SD Roth IRAs:</strong> Ideal for self-directed investors or real estate investors.</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Risk Management & Accessibility</h3>
                                    <ul className="styled-list">
                                        <li>Indexed annuities include <strong>surrender periods</strong> (5–10 years), where early withdrawal may result in <strong>penalties up to 10%</strong>.</li>
                                        <li>Some products offer <strong>liquidity riders</strong>, <strong>free withdrawal clauses</strong>, or <strong>RILA variations</strong> (Registered Indexed-Linked Annuities) with <strong>buffers and floors</strong> instead of traditional guarantees.</li>
                                    </ul>
                                    <p>Example: If the market falls 15% and your contract has a 10% buffer, your actual loss is 5%.</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Indexed Annuities vs. Fixed Income (Historical Comparison)</h3>
                                    <div className={`table-responsive ${expandedTables['historicalComparison'] ? 'expanded' : ''}`}>
                                        <table className="styled-table">
                                            <thead>
                                                <tr>
                                                    <th>Instrument</th>
                                                    <th>Avg Return (20 Yrs)</th>
                                                    <th>Tax Status</th>
                                                    <th>Principal Protected?</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Indexed Annuity (EIA)</td>
                                                    <td>4.5%–6.5%</td>
                                                    <td>Tax-deferred</td>
                                                    <td>Yes</td>
                                                </tr>
                                                <tr>
                                                    <td>CD (5yr)</td>
                                                    <td>2.0%–2.5%</td>
                                                    <td>Taxable</td>
                                                    <td>Yes</td>
                                                </tr>
                                                <tr>
                                                    <td>Treasury Bond (10yr)</td>
                                                    <td>3.0%–3.6%</td>
                                                    <td>Taxable</td>
                                                    <td>Yes</td>
                                                </tr>
                                                <tr>
                                                    <td>S&P 500 ETF</td>
                                                    <td>7.5%–9.0%</td>
                                                    <td>Taxable</td>
                                                    <td>No</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <button 
                                        className="expand-table-btn" 
                                        onClick={() => toggleTableExpand('historicalComparison')}
                                    >
                                        {expandedTables['historicalComparison'] ? 'Show Less' : 'Show More'} <FiChevronDown className={expandedTables['historicalComparison'] ? 'rotate' : ''} />
                                    </button>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box highlight-box">
                                    <h3>Final Thoughts</h3>
                                    <p>Indexed annuities offer a <strong>powerful hybrid</strong>: limited downside, upside potential, and long-term tax-deferred growth. They're not a replacement for equities, but they're increasingly essential in comprehensive plans. They pair well with <strong>real estate</strong>, <strong>trusts</strong>, <strong>IRAs</strong>, and <strong>insurance strategies</strong> to create holistic retirement and tax-efficient structures.</p>
                                </motion.div>
                            </motion.div>
                        )}

                        {/* Health Insurance Section */}
                        {activeSection === 'health' && (
                            <motion.div 
                                className="content-section"
                                initial="hidden"
                                animate="visible"
                                variants={sectionVariants}
                            >
                                <motion.div variants={fadeIn} className="section-header">
                                    <h2>Top Health Insurance Providers for Self-Employed & Small Businesses (2025)</h2>
                                </motion.div>

                                <motion.div variants={fadeIn} className="provider-cards">
                                    <div className="provider-card">
                                        <h3>1. UnitedHealthcare</h3>
                                        <ul className="styled-list">
                                            <li><strong>Best For:</strong> Nationwide coverage, large network</li>
                                            <li><strong>Offers:</strong> Self-employed, small biz, HSAs, telehealth</li>
                                            <li><strong>Extras:</strong> Digital tools, Level-funded options</li>
                                            <li><strong>Website:</strong> <a href="https://www.uhc.com" target="_blank" rel="noopener noreferrer">uhc.com</a></li>
                                        </ul>
                                    </div>

                                    <div className="provider-card">
                                        <h3>2. Blue Cross Blue Shield</h3>
                                        <ul className="styled-list">
                                            <li><strong>Best For:</strong> State-by-state plans, large provider access</li>
                                            <li><strong>Offers:</strong> Individual, family, group, dental/vision</li>
                                            <li><strong>Extras:</strong> Operates via state-level affiliates</li>
                                            <li><strong>Website:</strong> <a href="https://www.bcbs.com" target="_blank" rel="noopener noreferrer">bcbs.com</a></li>
                                        </ul>
                                    </div>

                                    <div className="provider-card">
                                        <h3>3. Aetna</h3>
                                        <ul className="styled-list">
                                            <li><strong>Best For:</strong> Wellness, CVS integration</li>
                                            <li><strong>Offers:</strong> Solo/self-employed, small biz, HSA-compatible</li>
                                            <li><strong>Extras:</strong> MinuteClinic, digital tools</li>
                                            <li><strong>Website:</strong> <a href="https://www.aetna.com" target="_blank" rel="noopener noreferrer">aetna.com</a></li>
                                        </ul>
                                    </div>

                                    <div className="provider-card">
                                        <h3>4. Kaiser Permanente</h3>
                                        <ul className="styled-list">
                                            <li><strong>Best For:</strong> Integrated care model</li>
                                            <li><strong>Offers:</strong> Individual & group (CA, CO, WA, etc.)</li>
                                            <li><strong>Extras:</strong> In-house providers, strong preventive care</li>
                                            <li><strong>Website:</strong> <a href="https://www.kaiserpermanente.org" target="_blank" rel="noopener noreferrer">kaiserpermanente.org</a></li>
                                        </ul>
                                    </div>

                                    <div className="provider-card">
                                        <h3>5. Humana</h3>
                                        <ul className="styled-list">
                                            <li><strong>Best For:</strong> Employee benefit integration</li>
                                            <li><strong>Offers:</strong> Medical, dental, vision, group health</li>
                                            <li><strong>Extras:</strong> Strong in Southern U.S., wellness tools</li>
                                            <li><strong>Website:</strong> <a href="https://www.humana.com" target="_blank" rel="noopener noreferrer">humana.com</a></li>
                                        </ul>
                                    </div>
                                </motion.div>
                                <ImageWrapper>
                                    <img 
                                        src="/assets/images/LHA3.png" 
                                        alt="Health insurance providers" 
                                        className="content-image" 
                                    />
                                    <ImageOverlay>
                                        <h4>Provider Highlights</h4>
                                        <ul>
                                            <li>UnitedHealthcare: Nationwide network.</li>
                                            <li>BCBS: State-specific plans.</li>
                                            <li>Aetna: Wellness focus.</li>
                                        </ul>
                                    </ImageOverlay>
                                </ImageWrapper>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h2>Health Insurance & HSA Strategies for Small Businesses and the Self-Employed</h2>
                                    <p>Health insurance is essential for protecting both personal and business finances. For small business owners and self-employed professionals, selecting the right plan-combined with tax-efficient strategies like Health Savings Accounts (HSAs)-can reduce risk and increase profitability.</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Why Health Insurance Matters for Business Owners</h3>
                                    <ul className="styled-list">
                                        <li>Protects against catastrophic medical costs</li>
                                        <li>Helps attract and retain top talent</li>
                                        <li>Reduces absenteeism and improves productivity</li>
                                        <li>Plans may be 100% tax-deductible for self-employed individuals</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Options for Health Insurance</h3>
                                    <h4>For Self-Employed Individuals:</h4>
                                    <ul className="styled-list">
                                        <li>Individual Marketplace Plans (via Healthcare.gov or state exchange)</li>
                                        <li>Monthly Premiums: $300-$800/month (varies by state, age, and coverage)</li>
                                        <li>Self-employed health insurance deduction may apply (IRS Form 1040)</li>
                                    </ul>

                                    <h4>For Small Businesses (2-50 employees):</h4>
                                    <ul className="styled-list">
                                        <li>Small Group Health Insurance (SHOP Market or private brokers)</li>
                                        <li>Average Employer Cost: $6,500/year per employee for single coverage</li>
                                        <li>Tax credit up to 50% of premiums if you meet certain criteria</li>
                                        <li>Option to use a PEO or HRA to manage benefits</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>What is a Health Savings Account (HSA)?</h3>
                                    <ul className="styled-list">
                                        <li>An HSA is a tax-advantaged account used to pay for qualified medical expenses</li>
                                        <li>Must be enrolled in a High Deductible Health Plan (HDHP) to qualify</li>
                                        <li>2025 Contribution Limits: $4,150 individual / $8,300 family + $1,000 catch-up over age 55</li>
                                        <li>Contributions are tax-deductible, grow tax-free, and can be withdrawn tax-free for medical expenses</li>
                                        <li>Unused funds roll over and can be invested</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h4>Tax Advantages of HSAs</h4>
                                    <ul className="styled-list">
                                        <li>Triple tax benefit: pre-tax contributions, tax-free growth, and tax-free withdrawals</li>
                                        <li>Can be used for dental, vision, prescriptions, co-pays, and more</li>
                                        <li>After age 65, funds can be withdrawn penalty-free for any purpose (ordinary income tax applies)</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Example Scenarios</h3>
                                    <div className="scenario-cards">
                                        <div className="scenario-card">
                                            <h4>Solo Owner with HDHP:</h4>
                                            <ul className="styled-list">
                                                <li>Premium: $425/month = $5,100/year</li>
                                                <li>HSA Contribution: $4,150 (deductible)</li>
                                                <li>Effective savings (22% tax rate): ~$913</li>
                                            </ul>
                                        </div>
                                        <div className="scenario-card">
                                            <h4>5-Employee Company with Group Plan:</h4>
                                            <ul className="styled-list">
                                                <li>Employer pays $350/month per employee</li>
                                                <li>Total cost: $21,000/year (deductible)</li>
                                                <li>May qualify for Small Business Health Care Tax Credit up to $10,500</li>
                                            </ul>
                                        </div>
                                    </div>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box highlight-box">
                                    <h3>Conclusion</h3>
                                    <p>Choosing the right health insurance plan and leveraging an HSA can lead to significant tax savings and financial protection. At Ravline Corp, we help small business owners implement scalable health insurance strategies that fit their budget, coverage needs, and tax situation.</p>
                                </motion.div>
                            </motion.div>
                        )}

                        {/* Keyman Insurance Section */}
                        {activeSection === 'keyman' && (
                            <motion.div 
                                className="content-section"
                                initial="hidden"
                                animate="visible"
                                variants={sectionVariants}
                            >
                                <motion.div variants={fadeIn} className="section-header">
                                    <h2>Keyman Insurance: Protecting the Lifeline of Your Business</h2>
                                    <p className="subtitle">Key Person Insurance is a powerful tool for safeguarding your business against the unexpected loss of a vital employee or executive.</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>What Is Keyman Insurance?</h3>
                                    <p>It's a life insurance policy a business takes out on a key employee or owner. The company is the beneficiary, and the proceeds can be used to:</p>
                                    <ul className="styled-list">
                                        <li>Offset revenue loss</li>
                                        <li>Hire and train a replacement</li>
                                        <li>Maintain operations during transition</li>
                                        <li>Pay off business loans tied to the key person</li>
                                    </ul>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/LHI2.jpg" 
                                            alt="Keyman insurance concept" 
                                            className="content-image" 
                                        />
                                        <ImageOverlay>
                                            <h4>Keyman Purpose</h4>
                                            <ul>
                                                <li>Offsets revenue loss.</li>
                                                <li>Funds replacement hiring.</li>
                                                <li>Ensures operational continuity.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Why It Matters</h3>
                                    <ul className="styled-list">
                                        <li>70% of small businesses are dependent on one or two individuals for operations.</li>
                                        <li>The average business takes <strong>6-12 months</strong> to fully recover from the loss of a key contributor.</li>
                                        <li>Without a strategy in place, this disruption can result in over <strong>$200,000</strong> in lost revenue and recruitment costs.</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Pricing Overview</h3>
                                    <ul className="styled-list">
                                        <li>Term Policy (Age 40, $1M coverage): $500 - $1,000/year</li>
                                        <li>Whole Life Policy (Cash Value): $2,000 - $5,000/year</li>
                                        <li>Premiums depend on age, health, type of policy, and coverage amount.</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <div className="pros-cons">
                                        <div className="pros">
                                            <h4>Pros</h4>
                                            <ul className="styled-list">
                                                <li>Financial safety net for unexpected loss</li>
                                                <li>Maintains investor, partner, and client confidence</li>
                                                <li>Supports continuity during succession</li>
                                                <li>Can help secure business loans or satisfy lender requirements</li>
                                                <li>Death benefits are generally <strong>income tax-free</strong></li>
                                            </ul>
                                        </div>
                                        <div className="cons">
                                            <h4>Cons</h4>
                                            <ul className="styled-list">
                                                <li>Premiums are <strong>not tax-deductible</strong> as a business expense</li>
                                                <li>Policy must be structured carefully to avoid misuse</li>
                                                <li>Requires employee consent (may involve HR/legal support)</li>
                                                <li>Transfer to insured employee can create tax liability</li>
                                            </ul>
                                        </div>
                                    </div>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Best Practices</h3>
                                    <ul className="styled-list">
                                        <li>Identify employees whose absence would severely affect business performance</li>
                                        <li>Work with a licensed insurance consultant or wealth advisor</li>
                                        <li>Align policy terms with your succession plan and continuity goals</li>
                                        <li>Keep documentation clear for internal stakeholders and external auditors</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box highlight-box">
                                    <h3>Conclusion</h3>
                                    <p>Keyman Insurance is not just a policy-it's a strategic layer of protection that shows leadership, planning, and fiscal responsibility. At Ravline Corp, we help business owners evaluate and implement this powerful insurance solution to secure their most valuable asset: people.</p>
                                </motion.div>
                            </motion.div>
                        )}

                        {/* Life Insurance Section */}
                        {activeSection === 'life' && (
                            <motion.div 
                                className="content-section"
                                initial="hidden"
                                animate="visible"
                                variants={sectionVariants}
                            >
                                <motion.div variants={fadeIn} className="section-header">
                                    <h2>Ravline Life Insurance Strategy Guide</h2>
                                    <p className="subtitle">Life insurance is more than a death benefit - it's a financial strategy for your family, your estate, and your future.</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Why Life Insurance Matters</h3>
                                    <ul className="styled-list">
                                        <li>60% of Americans have life insurance, but only 1 in 3 feel they have enough coverage.</li>
                                        <li>Funeral costs average between $7,000-$12,000.</li>
                                        <li>Life insurance can also be used as a wealth-building tool with cash value policies.</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Types of Life Insurance</h3>
                                    <div className="insurance-types">
                                        <div className="type-card">
                                            <h4>Term Life Insurance:</h4>
                                            <ul className="styled-list">
                                                <li>Affordable, temporary coverage (10, 20, 30 years)</li>
                                                <li>No cash value, expires at end of term</li>
                                            </ul>
                                        </div>
                                        <div className="type-card">
                                            <h4>Whole Life Insurance:</h4>
                                            <ul className="styled-list">
                                                <li>Permanent with fixed premiums and guaranteed cash value</li>
                                            </ul>
                                        </div>
                                        <div className="type-card">
                                            <h4>Universal Life Insurance:</h4>
                                            <ul className="styled-list">
                                                <li>Flexible premiums, cash value varies with market interest rates</li>
                                            </ul>
                                        </div>
                                        <div className="type-card">
                                            <h4>Indexed Universal Life (IUL):</h4>
                                            <ul className="styled-list">
                                                <li>Cash value linked to market indexes (e.g., S&P 500), growth capped</li>
                                            </ul>
                                        </div>
                                        <div className="type-card">
                                            <h4>Variable Universal Life (VUL):</h4>
                                            <ul className="styled-list">
                                                <li>Cash value tied to mutual fund-like subaccounts, high growth/risk</li>
                                            </ul>
                                        </div>
                                        <div className="type-card">
                                            <h4>Final Expense Insurance:</h4>
                                            <ul className="styled-list">
                                                <li>Small death benefit to cover burial/funeral costs ($5K-$25K)</li>
                                            </ul>
                                        </div>
                                        <div className="type-card">
                                            <h4>Joint Life Insurance:</h4>
                                            <ul className="styled-list">
                                                <li>Covers two people, pays on first or second death</li>
                                            </ul>
                                        </div>
                                        <div className="type-card">
                                            <h4>Mortgage Life Insurance:</h4>
                                            <ul className="styled-list">
                                                <li>Pays off remaining mortgage balance to lender</li>
                                            </ul>
                                        </div>
                                        <div className="type-card">
                                            <h4>Dependent Life Insurance:</h4>
                                            <ul className="styled-list">
                                                <li>Coverage for spouse or children</li>
                                            </ul>
                                        </div>
                                    </div>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/LHA5.png" 
                                            alt="Life insurance types" 
                                            className="content-image" 
                                        />
                                        <ImageOverlay>
                                            <h4>Life Insurance Types</h4>
                                            <ul>
                                                <li>Term: Temporary coverage.</li>
                                                <li>Whole: Permanent with cash value.</li>
                                                <li>IUL: Indexed growth.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Best Practices When Choosing a Policy</h3>
                                    <ul className="styled-list">
                                        <li>Work with a licensed financial consultant</li>
                                        <li>Consider age, health, debt, and long-term goals</li>
                                        <li>Decide whether you need pure protection or cash accumulation</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Tax & Wealth Strategy</h3>
                                    <ul className="styled-list">
                                        <li>Permanent life insurance grows tax-deferred</li>
                                        <li>Death benefit is income tax-free</li>
                                        <li>IUL policies allow tax-free loans for retirement</li>
                                        <li>Can be held in trusts for estate planning and income control</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box highlight-box">
                                    <h3>Conclusion</h3>
                                    <p>Life insurance is a powerful tool for protection and prosperity. At Ravline Corp, our licensed wealth consultants help you match the right product with your goals to protect your legacy and build financial security.</p>
                                </motion.div>
                            </motion.div>
                        )}
                    </div>
                </section>

                <Footer />
            </div >
        </div >
    );
}